import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Send, ShieldCheck, X } from "lucide-react";

import {
  LOCATION_OPTIONS,
  OPPORTUNITY_TYPES,
} from "../../constants/contactRequestOptions";
import { useSubmitContactRequest } from "../../hooks/queries/useContactRequests";

const INITIAL_FORM = {
  opportunityType: "",
  attentionReason: "",
  lookingFor: "",
  location: "",
  message: "",
};

const INITIAL_ERRORS = {
  opportunityType: "",
  attentionReason: "",
  location: "",
};

const getSubmitError = (error) => {
  if (error?.code === "duplicate_pending_request") {
    return "Ya tienes una solicitud pendiente con este atleta.";
  }
  if (error?.code === "guardian_info_missing") {
    return "Este atleta necesita la aprobación de su tutor, pero su información aún no está completa.";
  }
  if (error?.code === "API_UNREACHABLE") {
    return "No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.";
  }
  return error?.message || "No se pudo enviar la solicitud. Inténtalo de nuevo.";
};

// Scouting rows carry `age` at the top level; full athlete rows keep it in attributes.
const athleteAge = (athlete) => {
  const age = Number(athlete?.age ?? athlete?.attributes?.age);
  return Number.isFinite(age) ? age : 0;
};

const submittedMessage = (row, athleteName) => {
  const name = athleteName || "el atleta";
  if (row?.athlete_registered === false) {
    return row?.status === "guardian_pending"
      ? "Solicitud guardada y enviada al tutor del atleta para su revisión."
      : "Solicitud guardada. Este atleta aún no está registrado en la app; recibirá la notificación cuando se una.";
  }
  return row?.status === "guardian_pending"
    ? `Solicitud enviada. Como ${name} es menor de edad, primero la revisa su tutor legal. Te avisaremos en la app cuando responda.`
    : `Solicitud enviada. Te avisaremos en la app cuando ${name} responda.`;
};

const ContactRequestModal = ({ isOpen, onClose, athlete, onSubmitted }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [validationErrors, setValidationErrors] = useState(INITIAL_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const submitErrorId = useId();
  const submitContactRequest = useSubmitContactRequest();

  const athleteId = athlete?.athlete_id || athlete?.id || null;
  const isMinor = athleteAge(athlete) > 0 && athleteAge(athlete) < 18;

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => firstFieldRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM);
      setValidationErrors(INITIAL_ERRORS);
      setError("");
      setInfo("");
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
    if (field in INITIAL_ERRORS && value.trim()) {
      setValidationErrors((previous) => ({ ...previous, [field]: "" }));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const validate = () => {
    const nextErrors = {
      opportunityType: formData.opportunityType.trim()
        ? ""
        : "Selecciona el tipo de oportunidad.",
      attentionReason: formData.attentionReason.trim()
        ? ""
        : "Cuéntanos qué te llamó la atención.",
      location: formData.location.trim()
        ? ""
        : "Selecciona una ubicación.",
    };
    setValidationErrors(nextErrors);
    const firstInvalidField = Object.keys(nextErrors).find(
      (field) => nextErrors[field],
    );
    if (firstInvalidField) {
      dialogRef.current
        ?.querySelector(`[name="${firstInvalidField}"]`)
        ?.focus();
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !validate()) return;

    if (!athleteId) {
      setError("No se pudo identificar al atleta. Cierra esta ventana y vuelve a intentarlo.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setInfo("");

    try {
      const newRow = await submitContactRequest.mutateAsync({
        athleteId,
        opportunityType: formData.opportunityType.trim(),
        attentionReason: formData.attentionReason.trim(),
        lookingFor: formData.lookingFor.trim(),
        location: formData.location.trim(),
        message: formData.message.trim() || null,
      });

      onSubmitted?.(newRow);

      // Always confirm in place (with "Listo") instead of closing silently:
      // from Home or the athlete profile the only other feedback was the
      // button label changing after the refetch.
      setFormData(INITIAL_FORM);
      setInfo(submittedMessage(newRow, athlete?.full_name));
    } catch (submitError) {
      setError(getSubmitError(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClassName =
    "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 outline-none transition-colors focus:border-blue-700 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100";

  const renderFieldError = (field) =>
    validationErrors[field] ? (
      <p id={`error-${field}-${titleId}`} className="mt-1 text-sm text-red-700">
        {validationErrors[field]}
      </p>
    ) : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleClose();
          }}
          // Bottom sheet on phones: lift it above the iOS home indicator
          // (env() is 0 on the web, so desktop keeps its plain padding).
          style={{
            paddingTop: "max(0.5rem, env(safe-area-inset-top))",
            paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
          }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-2 backdrop-blur-sm sm:items-center sm:px-4"
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            initial={{ opacity: 0, scale: 0.98, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 16 }}
            className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
          >
            <div className="flex flex-none items-start justify-between gap-4 border-b border-gray-200 p-4 sm:p-6">
              <div className="min-w-0">
                <h2
                  id={titleId}
                  className="text-xl font-semibold uppercase tracking-wide text-gray-900"
                >
                  Contactar
                </h2>
                <p id={descriptionId} className="mt-1 break-words text-sm text-gray-600">
                  Motivo del contacto
                  {athlete?.full_name ? ` · ${athlete.full_name}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="-m-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:opacity-50"
                aria-label="Cerrar solicitud de contacto"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-4 sm:p-6">
                {isMinor && (
                  <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
                    <ShieldCheck size={18} className="mt-0.5 flex-none text-purple-700" aria-hidden="true" />
                    <p>
                      <span className="font-semibold">
                        {athlete?.full_name || "Este atleta"} es menor de edad.
                      </span>{" "}
                      Tu solicitud le llegará por correo a su tutor legal, que tiene 7 días
                      para aprobarla. Solo podrás escribirle si el tutor la aprueba.
                    </p>
                  </div>
                )}
                <div>
                  <label htmlFor={`opportunity-${titleId}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Tipo de oportunidad <span aria-hidden="true">*</span>
                  </label>
                  <select
                    ref={firstFieldRef}
                    id={`opportunity-${titleId}`}
                    name="opportunityType"
                    value={formData.opportunityType}
                    onChange={(event) => handleChange("opportunityType", event.target.value)}
                    aria-required="true"
                    aria-invalid={Boolean(validationErrors.opportunityType)}
                    aria-describedby={validationErrors.opportunityType ? `error-opportunityType-${titleId}` : undefined}
                    className={fieldClassName}
                  >
                    <option value="">Selecciona una opción</option>
                    {OPPORTUNITY_TYPES.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  {renderFieldError("opportunityType")}
                </div>

                <div>
                  <label htmlFor={`attention-${titleId}`} className="mb-1 block text-sm font-medium text-gray-700">
                    ¿Qué te llamó la atención del atleta? <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id={`attention-${titleId}`}
                    name="attentionReason"
                    type="text"
                    value={formData.attentionReason}
                    onChange={(event) => handleChange("attentionReason", event.target.value)}
                    maxLength={140}
                    aria-required="true"
                    aria-invalid={Boolean(validationErrors.attentionReason)}
                    aria-describedby={validationErrors.attentionReason ? `error-attentionReason-${titleId}` : `attention-count-${titleId}`}
                    placeholder="Ej: Su rendimiento en velocidad"
                    className={fieldClassName}
                  />
                  <div className="mt-1 flex items-start justify-between gap-3">
                    {renderFieldError("attentionReason") || <span />}
                    <span id={`attention-count-${titleId}`} className="text-xs text-gray-500">{formData.attentionReason.length}/140</span>
                  </div>
                </div>

                <div>
                  <label htmlFor={`looking-${titleId}`} className="mb-1 block text-sm font-medium text-gray-700">¿Qué estás buscando?</label>
                  <input
                    id={`looking-${titleId}`}
                    name="lookingFor"
                    type="text"
                    value={formData.lookingFor}
                    onChange={(event) => handleChange("lookingFor", event.target.value)}
                    maxLength={140}
                    placeholder="Opcional"
                    className={fieldClassName}
                  />
                  <p className="mt-1 text-right text-xs text-gray-500">{formData.lookingFor.length}/140</p>
                </div>

                <div>
                  <label htmlFor={`location-${titleId}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Ubicación <span aria-hidden="true">*</span>
                  </label>
                  <select
                    id={`location-${titleId}`}
                    name="location"
                    value={formData.location}
                    onChange={(event) => handleChange("location", event.target.value)}
                    aria-required="true"
                    aria-invalid={Boolean(validationErrors.location)}
                    aria-describedby={validationErrors.location ? `error-location-${titleId}` : undefined}
                    className={fieldClassName}
                  >
                    <option value="">Selecciona una ubicación</option>
                    {LOCATION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  {renderFieldError("location")}
                </div>

                <div>
                  <label htmlFor={`message-${titleId}`} className="mb-1 block text-sm font-medium text-gray-700">Mensaje introductorio</label>
                  <textarea
                    id={`message-${titleId}`}
                    name="message"
                    value={formData.message}
                    onChange={(event) => handleChange("message", event.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Escribe un mensaje para el atleta (opcional)"
                    className={`${fieldClassName} resize-none`}
                  />
                  <p className="mt-1 text-right text-xs text-gray-500">{formData.message.length}/500</p>
                </div>

                {error && (
                  <p id={submitErrorId} role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}
                {info && (
                  <p role="status" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                    {info}
                  </p>
                )}
              </div>

              <div className="flex flex-none items-center justify-end gap-3 border-t border-gray-200 bg-white p-4 sm:p-6">
                {info ? (
                  <button type="button" onClick={handleClose} className="min-h-11 rounded-lg bg-blue-950 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2">
                    Listo
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={handleClose} disabled={isSubmitting} className="min-h-11 rounded-lg px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:opacity-50">
                      Cancelar
                    </button>
                    <button type="submit" disabled={isSubmitting} aria-describedby={error ? submitErrorId : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-blue-950 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">
                      {isSubmitting ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
                      {isSubmitting ? "Enviando..." : "Enviar"}
                    </button>
                  </>
                )}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactRequestModal;
